import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { ImportedModelObjectMetadataService } from '../../metadata'

/**
 * 完整 GLB 原始显示 Mesh 来源定位绑定器。
 *
 * GLTFLoader 实例化后仍通过 parser associations 保留 Object3D 到原始 node、mesh definition
 * 与 primitive 的对应关系。该绑定器在拓扑 host 等派生 Mesh 创建之前捕获对应关系，并通过
 * ImportedModelObjectMetadataService 写入 JSON 安全元数据，供后续离线包导出生成稳定显示身份。
 */
export class CompleteGlbDisplaySourceMetadataBinder {
  /**
   * 为当前投影场景中的原始 glTF Mesh 绑定稳定来源定位。
   *
   * @param gltf GLTFLoader 已完成实例化的结果。
   * @returns 成功绑定来源定位的原始 Mesh 数量。
   */
  public bind(gltf: GLTF): number {
    const associationValue = (gltf.parser as unknown as { associations?: unknown }).associations
    if (!(associationValue instanceof Map)) {
      throw new Error('完整 GLB 显示来源绑定失败：GLTFLoader parser 缺少 associations')
    }
    const associations = associationValue as ReadonlyMap<
      THREE.Object3D,
      PuppyCAD.CompleteGlbParserAssociation | undefined
    >
    const sourceNodes = (gltf.parser.json as PuppyCAD.CompleteGlbJsonDocument).nodes ?? []
    let bindingCount = 0

    gltf.scene.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) {
        return
      }

      // 派生 topology host、边线和编辑器辅助 Mesh 没有 parser association，不能冒充源显示实例。
      const ownAssociation = associations.get(object)
      if (!ownAssociation) {
        return
      }
      const nodeAssociation = this.resolveNodeAssociation(object, associations)
      const nodeIndex = this.readNonNegativeInteger(nodeAssociation?.nodes)
      if (nodeIndex === null || !sourceNodes[nodeIndex]) {
        return
      }

      const sourceNode = sourceNodes[nodeIndex]
      const meshIndex = this.readNonNegativeInteger(ownAssociation.meshes)
        ?? this.readNonNegativeInteger(sourceNode.mesh)
      const primitiveIndex = this.readNonNegativeInteger(ownAssociation.primitives) ?? 0
      ImportedModelObjectMetadataService.writeMetadata(object, {
        displaySource: {
          kind: 'complete-glb',
          nodeIndex,
          ...(meshIndex !== null ? { meshIndex } : {}),
          primitiveIndex,
        },
      })
      bindingCount += 1
    })

    return bindingCount
  }

  /**
   * 从 Mesh 自身或最近父级读取原始 glTF node association。
   *
   * 多 primitive node 由父 Group 承载 node association，各子 Mesh 只承载 mesh/primitive
   * association，因此必须沿原始父链组合两部分定位。
   *
   * @param object 当前原始渲染 Mesh。
   * @param associations GLTFLoader 对象关联表。
   * @returns 最近的 node association；不存在时返回 undefined。
   */
  private resolveNodeAssociation(
    object: THREE.Object3D,
    associations: ReadonlyMap<
      THREE.Object3D,
      PuppyCAD.CompleteGlbParserAssociation | undefined
    >,
  ): PuppyCAD.CompleteGlbParserAssociation | undefined {
    let current: THREE.Object3D | null = object
    while (current) {
      const association = associations.get(current)
      if (this.readNonNegativeInteger(association?.nodes) !== null) {
        return association
      }
      current = current.parent
    }
    return undefined
  }

  /**
   * 读取非负整数索引。
   *
   * @param value parser association 或 glTF definition 中的未知值。
   * @returns 有效索引；无效时返回 null。
   */
  private readNonNegativeInteger(value: unknown): number | null {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0
      ? value
      : null
  }
}
